import React, { useEffect, useState } from 'react';

import _ from 'lodash';
import {
  Table,
  Form,
  Button,
  Popconfirm,
  notification,
  Drawer,
  Select,
  Typography,
  Input,
  Spin,
  Modal,
  Row,
  Col,
} from 'antd';
import { useSelector } from 'react-redux';

import useApiGateway from '../../../apiGateway';
import getOr from '../../../helpers/getOr';
import { AccessNotice } from '../../../components';

export default function userManagement() {
  const apiGateway = useApiGateway();

  const [dataSource, setDataSource] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editableUser, setEditableUser] = useState(null);
  const [userAccessDrawerVisible, setUserAccessDrawerVisible] = useState(null);
  const [dsps, setDsps] = useState(null);
  const [ssps, setSsps] = useState(null);
  const [companies, setCompanies] = useState(null);
  const [userAccesses, setUserAccesses] = useState(null);
  const [userAccessUser, setUserAccessUser] = useState(null);
  const [userAccessSpinning, setUserAccessSpinning] = useState(false);
  const [plaintextToken, setPlaintextToken] = useState(null);
  const [plaintextTokenUser, setPlaintextTokenUser] = useState(null);

  const userInfo = useSelector((state) => state.login);

  const getData = async () => {
    const allowedRoles =
      userInfo.role === 'admin' ? ['superUser', 'superUserLimited', 'localAdmin'] : ['superUser', 'superUserLimited'];
    // The API already limits the list to the caller's exchange
    const users = await apiGateway.users.getAll({ allowedRoles });
    const dspsResponse = await apiGateway.dsps.getAll({ includeSpend: false });
    const sspsResponse = await apiGateway.ssps.getAll({ includeSpend: false });
    const companiesResponse = await apiGateway.users.getAll({
      allowedRoles: ['user'],
    });

    setCompanies(companiesResponse);
    setDsps(dspsResponse);
    setSsps(sspsResponse);
    setDataSource(users);
  };

  useEffect(() => {
    if (!userInfo || !userInfo.role) return;
    (async () => {
      await getData();
    })();
  }, [userInfo.role]);

  const createNewUserDrawer = () => {
    setEditableUser({
      firstName: null,
      lastName: null,
      emailAddress: null,
      login: null,
      exchangeId: userInfo.exchangeId,
      role: 'superUser',
      permission: 'create',
      password: null,
    });
    setDrawerVisible(true);
  };

  const useTable = () => {
    const deleteUser = async (user) => {
      const userId = user.id;

      try {
        await apiGateway.users.deleteUser(userId);

        notification.success({
          title: 'User deleted',
          description: `User with email ${user.emailAddress} was successfully deleted`,
        });
      } catch (e) {
        notification.error({
          title: 'Error when deleting user',
          description: e.message,
        });
      } finally {
        await getData();
      }
    };

    const editUserAccess = async (user) => {
      let userAccessesResponse = await apiGateway.users.getUserAccess(user.id);
      userAccessesResponse = getOr(userAccessesResponse, 'data', []);

      userAccessesResponse = {
        original: {
          dsps: _(userAccessesResponse)
            .filter((x) => x.dspId)
            .filter(_.identity)
            .uniq()
            .value(),
          ssps: _(userAccessesResponse)
            .filter((x) => x.sspId)
            .filter(_.identity)
            .uniq()
            .value(),
          companies: _(userAccessesResponse)
            .filter((x) => x.companyId)
            .filter(_.identity)
            .uniq()
            .value(),
        },
        dsps: _(userAccessesResponse)
          .map((x) => x.dspId)
          .filter(_.identity)
          .uniq()
          .value(),
        ssps: _(userAccessesResponse)
          .map((x) => x.sspId)
          .filter(_.identity)
          .uniq()
          .value(),
        companies: _(userAccessesResponse)
          .map((x) => x.companyId)
          .filter(_.identity)
          .uniq()
          .value(),
      };
      setUserAccesses(userAccessesResponse);
      setUserAccessDrawerVisible(true);
      setUserAccessUser(user);
    };

    const columns = [
      {
        title: 'ID',
        dataIndex: 'id',
        width: 80,
      },
      {
        title: 'Email',
        dataIndex: 'emailAddress',
        width: 260,
        ellipsis: true,
      },
      {
        title: 'Login',
        dataIndex: 'login',
        width: 200,
        ellipsis: true,
      },
      {
        title: 'Role',
        width: 160,
        render: (user) => {
          if (user.role === 'superUserLimited') {
            return (
              <Button type="link" onClick={() => editUserAccess(user)} style={{ margin: 0, padding: 0 }}>
                Limited Manager
              </Button>
            );
          }

          if (user.role === 'superUser') {
            return 'Manager';
          }

          if (user.role === 'localAdmin') {
            return 'Local Admin';
          }

          if (user.role === 'admin') {
            return 'Admin';
          }

          return user.role;
        },
      },
      {
        title: 'Permission',
        dataIndex: 'permission',
        width: 120,
      },
      {
        title: 'Action',
        width: 160,
        render: (user) => {
          const props = {
            title: 'Are you sure delete this user?',
            okText: 'Yes',
            cancelText: 'No',
            onConfirm: () => deleteUser(user),
          };

          const editUser = () => {
            setDrawerVisible(true);
            setEditableUser(user);
          };

          return (
            <>
              <Button type="link" onClick={editUser}>
                Edit
              </Button>
              <Popconfirm {...props}>
                <Button danger type="text">
                  Delete
                </Button>
              </Popconfirm>
            </>
          );
        },
      },
    ];

    const tableProps = {
      dataSource,
      columns,
      rowKey: 'id',
      pagination: false,
      loading: !dataSource || !dataSource.length,
      size: 'middle',
      scroll: { x: 'max-content', y: 600 },
    };

    return <Table {...tableProps} />;
  };

  const useUserAccessDrawer = () => {
    const updateUserAccess = (type, values) => {
      if (type === 'dsp') {
        setUserAccesses({
          ...userAccesses,
          dsps: values,
        });
      }
      if (type === 'ssp') {
        setUserAccesses({
          ...userAccesses,
          ssps: values,
        });
      }
      if (type === 'company') {
        setUserAccesses({
          ...userAccesses,
          dsps: userAccesses.dsps.filter((dspId) => {
            const dsp = dsps.find((x) => x.id === dspId);
            return values.includes(dsp.partnerId);
          }),
          ssps: userAccesses.ssps.filter((sspId) => {
            const ssp = ssps.find((x) => x.id === sspId);
            return values.includes(ssp.partnerId);
          }),
          companies: values,
        });
      }
    };

    const closeAccessDrawer = () => {
      setUserAccessDrawerVisible(false);
      setUserAccesses(null);
      setUserAccessUser(null);
    };

    const submitAccessDrawer = async () => {
      setUserAccessSpinning(true);

      try {
        const toCreate = [
          ...getOr(userAccesses, 'dsps', []).map((dspId) => ({ dspId })),
          ...getOr(userAccesses, 'ssps', []).map((sspId) => ({ sspId })),
          ...getOr(userAccesses, 'companies', []).map((companyId) => ({ companyId })),
        ].map((x) => ({ ...x, userId: userAccessUser.id }));

        await apiGateway.users.createUserAccesses(toCreate);

        const toDelete = _([
          ...getOr(userAccesses, 'original.dsps', [])
            .map(({ id, dspId }) => {
              const del = !getOr(userAccesses, 'dsps', [dspId]).includes(dspId);
              return del ? id : null;
            })
            .filter(_.identity),
          ...getOr(userAccesses, 'original.ssps', [])
            .map(({ id, sspId }) => {
              const del = !getOr(userAccesses, 'ssps', [sspId]).includes(sspId);
              return del ? id : null;
            })
            .filter(_.identity),
          ...getOr(userAccesses, 'original.companies', [])
            .map(({ id, companyId }) => {
              const del = !getOr(userAccesses, 'companies', [companyId]).includes(companyId);
              return del ? id : null;
            })
            .filter(_.identity),
        ])
          .uniq()
          .value();

        await apiGateway.users.deleteUserAccesses(toDelete);

        notification.success({
          title: 'User access',
          description: 'User access was successfully updated',
        });

        closeAccessDrawer();
      } catch (e) {
        notification.error({
          title: 'Error when updating user access',
          description: e.message,
        });
      } finally {
        setUserAccessSpinning(false);
        await getData();
      }
    };

    const userAccessDrawerProps = {
      title: 'Edit user access',
      open: userAccessDrawerVisible,
      size: 720,
      onClose: closeAccessDrawer,
      footer: (
        <Spin spinning={userAccessSpinning}>
          <div
            style={{
              textAlign: 'right',
            }}
          >
            <Button onClick={closeAccessDrawer} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button onClick={submitAccessDrawer} type="primary">
              Submit
            </Button>
          </div>
        </Spin>
      ),
    };

    return (
      userAccessDrawerVisible && (
        <Drawer {...userAccessDrawerProps}>
          <Form.Item>
            <Typography.Title level={4}>DSPs Access</Typography.Title>
            <Select
              mode="multiple"
              onChange={(val) => updateUserAccess('dsp', val)}
              defaultValue={(userAccesses || []).dsps}
              value={(userAccesses || []).dsps}
              options={(dsps || [])
                .filter((x) => x && userAccessUser)
                .filter((x) => x.exchangeId === userAccessUser.exchangeId)
                .map((x) => ({ label: x.name, value: x.id }))}
            />
          </Form.Item>
          <Form.Item>
            <Typography.Title level={4}>SSPs Access</Typography.Title>
            <Select
              mode="multiple"
              onChange={(val) => updateUserAccess('ssp', val)}
              defaultValue={(userAccesses || []).ssps}
              value={(userAccesses || []).ssps}
              options={(ssps || [])
                .filter((x) => x && userAccessUser)
                .filter((x) => x.exchangeId === userAccessUser.exchangeId)
                .map((x) => ({ label: x.name, value: x.id }))}
            />
          </Form.Item>
          <Form.Item>
            <Typography.Title level={4}>Companies Access</Typography.Title>
            <Select
              mode="multiple"
              onChange={(val) => updateUserAccess('company', val)}
              value={(userAccesses || []).companies}
              defaultValue={(userAccesses || []).companies}
              options={(companies || [])
                .filter((x) => x && userAccessUser)
                .filter((x) => x.exchangeId === userAccessUser.exchangeId)
                .map((x) => ({ label: x.firstName, value: x.id }))}
            />
          </Form.Item>
        </Drawer>
      )
    );
  };

  const usePlaintextTokenModal = () => {
    const close = () => {
      setPlaintextToken(null);
      setPlaintextTokenUser(null);
    };

    const copy = async () => {
      try {
        await navigator.clipboard.writeText(plaintextToken);
        notification.success({ title: 'Token copied to clipboard' });
      } catch (e) {
        notification.error({ title: 'Copy failed', description: e.message });
      }
    };

    return (
      <Modal
        title="New token generated"
        open={Boolean(plaintextToken)}
        onCancel={close}
        onOk={close}
        okText="Close"
        cancelButtonProps={{ style: { display: 'none' } }}
        width={600}
      >
        <p>
          <strong>This token will not be shown again.</strong> Copy it now and share it securely with{' '}
          {plaintextTokenUser ? plaintextTokenUser.emailAddress : 'the user'}.
        </p>
        <Input.TextArea value={plaintextToken || ''} readOnly autoSize style={{ fontFamily: 'monospace' }} />
        <div style={{ textAlign: 'right', marginTop: 12 }}>
          <Button onClick={copy} type="primary">
            Copy to clipboard
          </Button>
        </div>
      </Modal>
    );
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setEditableUser(null);
  };

  const updateUser = async () => {
    try {
      // A user never changes exchange, so the exchange is not sent on update
      await apiGateway.users.updateUser(_.omit(editableUser, ['exchangeId', 'exchangeName']));

      notification.success({
        title: 'User updated',
        description: `User with email ${editableUser.emailAddress} was successfully updated`,
      });

      closeDrawer();
    } catch (e) {
      notification.error({
        title: 'Error when updating user',
        description: e.message,
      });
    } finally {
      await getData();
    }
  };

  const createUser = async () => {
    try {
      await apiGateway.users.createUser(editableUser);

      notification.success({
        title: 'User created',
        description: 'User was successfully created',
      });

      closeDrawer();
    } catch (e) {
      notification.error({
        title: 'Error when creating user',
        description: e.message,
      });
    } finally {
      await getData();
    }
  };

  const useEditUserDrawer = () => {
    const upsertUser = () => {
      if (editableUser.id) {
        return updateUser();
      }
      return createUser();
    };

    const disable2fa = async () => {
      try {
        await apiGateway.users.disable2fa(editableUser.id);
      } catch (e) {
        notification.error({
          title: 'Error when disabling 2FA',
          description: e.message,
        });
      } finally {
        window.location.reload();
      }
    };

    const refreshEditableUserToken = async () => {
      try {
        const response = await apiGateway.users.getToken(editableUser.id);
        const data = (response && response.data) || {};
        setEditableUser((prev) =>
          prev
            ? {
                ...prev,
                hasToken: Boolean(data.exists),
                tokenCreatedAt: data.createdAt || null,
                tokenLastUsedAt: data.lastUsedAt || null,
              }
            : prev
        );
      } catch (e) {
        // best-effort; the table refresh below will still pick up the new state
      }
    };

    const generateTokenForEditableUser = async () => {
      try {
        const response = await apiGateway.users.generateToken(editableUser.id);
        const token = (response && response.data && response.data.token) || null;
        if (!token) {
          throw new Error('Empty token response');
        }
        setPlaintextTokenUser(editableUser);
        setPlaintextToken(token);
        await refreshEditableUserToken();
      } catch (e) {
        notification.error({
          title: 'Error when generating token',
          description: e.message,
        });
      } finally {
        await getData();
      }
    };

    const deleteTokenForEditableUser = async () => {
      try {
        await apiGateway.users.deleteToken(editableUser.id);
        notification.success({
          title: 'Token deleted',
          description: `Token for ${editableUser.emailAddress} was removed`,
        });
        await refreshEditableUserToken();
      } catch (e) {
        notification.error({
          title: 'Error when deleting token',
          description: e.message,
        });
      } finally {
        await getData();
      }
    };

    const drawerProps = {
      title: 'Edit user',
      open: drawerVisible,
      size: 720,
      onClose: closeDrawer,
      footer: (
        <div
          style={{
            textAlign: 'right',
          }}
        >
          <Button onClick={closeDrawer} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button onClick={upsertUser} type="primary">
            Submit
          </Button>
        </div>
      ),
    };

    return (
      editableUser && (
        <Drawer {...drawerProps}>
          <Form layout="vertical">
            <Row>
              <Col xs={24}>
                <Form.Item
                  label="Login"
                  name="login"
                  onChange={(e) => setEditableUser({ ...editableUser, login: e.target.value })}
                  rules={[
                    {
                      required: true,
                      message: 'Please input login!',
                    },
                  ]}
                >
                  <Input value={editableUser.login} defaultValue={editableUser.login} />
                </Form.Item>
                <Form.Item
                  label="Email"
                  name="emailAddress"
                  value={editableUser.emailAddress}
                  onChange={(e) => setEditableUser({ ...editableUser, emailAddress: e.target.value })}
                  rules={[
                    {
                      required: true,
                      message: 'Please input email!',
                    },
                  ]}
                >
                  <Input value={editableUser.emailAddress} defaultValue={editableUser.emailAddress} />
                </Form.Item>
                <Form.Item
                  label="Password"
                  name="password"
                  onChange={(e) => setEditableUser({ ...editableUser, password: e.target.value })}
                  rules={[
                    {
                      required: true,
                      message: 'Please input password!',
                    },
                  ]}
                >
                  <Input value={editableUser.password} defaultValue={editableUser.password} />
                </Form.Item>
                <Form.Item
                  label="First Name"
                  name="firstName"
                  value={editableUser.firstName}
                  onChange={(e) => setEditableUser({ ...editableUser, firstName: e.target.value })}
                  rules={[
                    {
                      required: true,
                      message: 'Please input first name!',
                    },
                  ]}
                >
                  <Input value={editableUser.firstName} defaultValue={editableUser.firstName} />
                </Form.Item>
                <Form.Item
                  label="Last Name"
                  name="lastName"
                  value={editableUser.lastName}
                  onChange={(e) => setEditableUser({ ...editableUser, lastName: e.target.value })}
                  rules={[
                    {
                      required: true,
                      message: 'Please input last name!',
                    },
                  ]}
                >
                  <Input value={editableUser.lastName} defaultValue={editableUser.lastName} />
                </Form.Item>
                <Form.Item>
                  <Typography.Title level={4}>Role</Typography.Title>
                  <Select
                    defaultValue="superUser"
                    onChange={(val) => setEditableUser({ ...editableUser, role: val })}
                    value={editableUser.role}
                  >
                    <Select.Option value="superUser">Manager</Select.Option>
                    <Select.Option value="superUserLimited">Limited Manager</Select.Option>
                    <Select.Option value="localAdmin">Local Admin</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item>
                  <Typography.Title level={4}>Permission</Typography.Title>
                  <Select
                    defaultValue="create"
                    onChange={(val) => setEditableUser({ ...editableUser, permission: val })}
                    value={editableUser.permission}
                  >
                    <Select.Option value="create">Create</Select.Option>
                    <Select.Option value="view">View</Select.Option>
                  </Select>
                </Form.Item>
                {editableUser && editableUser.id && (
                  <Form.Item>
                    <Typography.Title level={4}>MCP Token</Typography.Title>
                    {!editableUser.hasToken ? (
                      <Button onClick={generateTokenForEditableUser}>Generate Token</Button>
                    ) : (
                      <div>
                        <div style={{ fontSize: 12 }}>
                          Created:{' '}
                          {editableUser.tokenCreatedAt ? new Date(editableUser.tokenCreatedAt).toLocaleString() : ''}
                        </div>
                        <div style={{ fontSize: 12, marginBottom: 8 }}>
                          Last used:{' '}
                          {editableUser.tokenLastUsedAt
                            ? new Date(editableUser.tokenLastUsedAt).toLocaleString()
                            : 'Never'}
                        </div>
                        <Popconfirm
                          title="Regenerate the token? The current token will be invalidated immediately."
                          okText="Yes"
                          cancelText="No"
                          onConfirm={generateTokenForEditableUser}
                        >
                          <Button style={{ marginRight: 8 }}>Regenerate</Button>
                        </Popconfirm>
                        <Popconfirm
                          title="Delete this token? The user will lose MCP access."
                          okText="Yes"
                          cancelText="No"
                          onConfirm={deleteTokenForEditableUser}
                        >
                          <Button danger>Delete</Button>
                        </Popconfirm>
                      </div>
                    )}
                  </Form.Item>
                )}
                {(editableUser && editableUser.twoFaSecret && (
                  <Form.Item>
                    <Button type="primary" onClick={disable2fa}>
                      Disable 2FA
                    </Button>
                  </Form.Item>
                )) ||
                  ''}
              </Col>
            </Row>
          </Form>
        </Drawer>
      )
    );
  };

  if (userInfo && !['admin', 'localAdmin'].includes(userInfo.role)) {
    return <div>You do not have access to this page</div>;
  }

  return (
    <div className="xchange-page xchange-page--narrow">
      <AccessNotice level="admin" />

      <Row>
        <Col xs={24} style={{ textAlign: 'right', marginBottom: 16 }}>
          <Button onClick={createNewUserDrawer}>New User</Button>
        </Col>
        <Col xs={24}>{useTable()}</Col>

        {useUserAccessDrawer()}
        {useEditUserDrawer()}
        {usePlaintextTokenModal()}
      </Row>
    </div>
  );
}
